// Lists: a day of service calls goes in two ways — a picture or PDF the app reads for you, or the
// calls typed in by hand — the office checks the rows, picks a template and a time, and the app
// schedules the texts (every one is a normal scheduled_messages row, so every rule still applies).

import { useRef, useState, type DragEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, money, type ListImport, type MeResponse } from '../../api';
import { Loading, ErrorBox, Empty, InlineError } from '../../components/States';
import { fmtFull, pluralize } from '../../lib/format';

const MAX_FILES = 10;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];

export const IMPORT_STATUS_WORDS: Record<ListImport['status'], string> = {
  extracting: 'Reading',
  review: 'Ready to check',
  scheduled: 'Scheduled',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

export function importTone(s: ListImport['status']): string {
  if (s === 'scheduled') return 'ok';
  if (s === 'review') return 'accent';
  if (s === 'failed') return 'danger';
  if (s === 'extracting') return 'warn';
  return '';
}

/** Why a file cannot be read, in plain words; null when it is fine. */
export function fileProblem(f: File): string | null {
  const name = f.name.toLowerCase();
  if (f.type === 'image/heic' || f.type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif')) {
    return 'HEIC photos cannot be read here. On the phone, take a screenshot of the list and upload the screenshot instead.';
  }
  if (!ACCEPT.includes(f.type) && !/\.(png|jpe?g|webp|pdf)$/.test(name)) return 'Only PNG, JPG, WEBP or PDF.';
  if (f.size > MAX_BYTES) return `Too big (${(f.size / 1024 / 1024).toFixed(1)} MB). The limit is 10 MB.`;
  return null;
}

export function ListsPage({ me }: { me: MeResponse }) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['imports'],
    queryFn: api.imports.list,
    refetchInterval: (query) => (query.state.data?.items.some((i) => i.status === 'extracting') ? 3000 : false),
  });
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const addFiles = (list: FileList | File[] | null) => {
    if (!list) return;
    const next = [...files];
    const errs: string[] = [];
    for (const f of Array.from(list)) {
      const why = fileProblem(f);
      if (why) {
        errs.push(`${f.name}: ${why}`);
        continue;
      }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      if (next.length >= MAX_FILES) {
        errs.push(`Up to ${MAX_FILES} files per list. ${f.name} was left out.`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
    setProblems(errs);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    addFiles(e.dataTransfer.files);
  };

  const create = useMutation({
    mutationFn: () => api.imports.create(files, note),
    onSuccess: (r) => {
      setFiles([]);
      setNote('');
      navigate(`/lists/${r.import.id}`);
    },
  });

  // the same list, only typed instead of read: it opens on the same check-and-schedule table
  const typeIn = useMutation({
    mutationFn: () => api.imports.createManual(),
    onSuccess: (r) => navigate(`/lists/${r.import.id}`),
  });

  const items = q.data?.items ?? [];
  const tz = me.workspace.timezone;

  return (
    <div className="page">
      <div className="page-head">
        <h1>Lists</h1>
        <span className="muted small">A day of service calls in, one text each out.</span>
      </div>

      <div className="card">
        <h2>Type tomorrow's calls</h2>
        <p className="muted small">
          Start an empty list and type each stop: name, phone, address, and the time or the time frame you gave them. No picture needed.
        </p>
        <InlineError error={typeIn.error} />
        <button className="btn primary" disabled={typeIn.isPending} onClick={() => typeIn.mutate()}>
          {typeIn.isPending ? 'Opening' : 'Type them in'}
        </button>
      </div>

      <div className="card">
        <h2>Or read a list from a picture</h2>
        <p className="muted small">Screenshots, photos (PNG, JPG, WEBP) or PDFs of the list. Up to {MAX_FILES} files, 10 MB each. Reading takes about a minute.</p>
        <div
          className={`drop-zone ${over ? 'over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        >
          Drop files here, or click to choose
          <input ref={inputRef} type="file" multiple accept=".png,.jpg,.jpeg,.webp,.pdf,image/png,image/jpeg,image/webp,application/pdf" style={{ display: 'none' }} onChange={(e) => addFiles(e.target.files)} />
        </div>
        {problems.length > 0 && (
          <div className="notice warn" style={{ marginTop: 8 }}>
            {problems.map((p, i) => (
              <div key={i}>{p}</div>
            ))}
          </div>
        )}
        {files.length > 0 && (
          <ul className="file-list">
            {files.map((f) => (
              <li key={f.name + f.size}>
                <span>
                  {f.name} <span className="muted">({(f.size / 1024).toFixed(0)} KB)</span>
                </span>
                <button className="btn link small" onClick={() => setFiles(files.filter((x) => x !== f))}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <label className="field" style={{ marginTop: 12 }}>
          <span>Note for the reader (optional)</span>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. second column is the appointment time; skip the crossed-out rows" />
        </label>
        <InlineError error={create.error} />
        <button className="btn primary" disabled={files.length === 0 || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'Uploading' : 'Read the list'}
        </button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {q.isPending && <Loading />}
        {q.isError && (
          <div style={{ padding: 16 }}>
            <ErrorBox error={q.error} retry={() => q.refetch()} />
          </div>
        )}
        {q.isSuccess && items.length === 0 && <Empty title="No lists yet" hint="Type tomorrow's calls, or upload a picture of the list." />}
        {items.length > 0 && (
          <div className="table-wrap stack">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Where it came from</th>
                  <th>Rows</th>
                  <th>Status</th>
                  <th>AI cost</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id}>
                    <td className="nowrap" data-label="When">{fmtFull(i.created_at, tz)}</td>
                    <td data-label="From">
                      {i.source === 'manual' ? 'Typed in' : pluralize(i.asset_ids?.length ?? i.assets?.length ?? 0, 'file')}
                      {i.note && <div className="tiny muted">{i.note}</div>}
                    </td>
                    <td data-label="Rows">{i.status === 'extracting' ? '…' : i.rows?.length ?? 0}</td>
                    <td data-label="Status">
                      <span className={`chip ${importTone(i.status)}`}>{IMPORT_STATUS_WORDS[i.status] ?? i.status}</span>
                      {i.status === 'scheduled' && i.scheduled_count != null && <div className="tiny muted">{pluralize(i.scheduled_count, 'text')} scheduled</div>}
                      {i.status === 'failed' && i.error && <div className="tiny danger-text">{i.error}</div>}
                    </td>
                    <td className="nowrap" data-label="AI cost">{i.source === 'manual' ? <span className="faint">—</span> : `$${money(i.cost_usd).toFixed(2)}`}</td>
                    <td className="right">
                      <Link to={`/lists/${i.id}`} className="btn small">
                        {i.status === 'review' ? 'Check and schedule' : 'Open'}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
